import { useCallback, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Volume2 } from 'lucide-react';
import { Reveal } from '@/components/Reveal';
import { TextReveal } from '@/components/TextReveal';
import { MagneticButton } from '@/components/MagneticButton';
import { SEO } from '@/components/SEO';
import { AudienceToggle } from '@/components/howItWorks/AudienceToggle';
import { DualTrackTimeline } from '@/components/howItWorks/DualTrackTimeline';
import { VoiceGuidePlayer } from '@/components/howItWorks/VoiceGuidePlayer';
import { PlatformTourCarousel } from '@/components/howItWorks/PlatformTourCarousel';
import { HOW_IT_WORKS } from '@/constants/testIds';
import { STAGES } from '@/lib/howItWorksContent';

export default function HowItWorks() {
  const [audience, setAudience] = useState('client');
  const [activeStage, setActiveStage] = useState(STAGES[0].slug);
  const timelineRef = useRef(null);

  const goToStage = useCallback((slug) => {
    setActiveStage(slug);
    timelineRef.current?.scrollToStage(slug);
  }, []);

  return (
    <div>
      <SEO
        title="How It Works"
        description="See a stage-by-stage look at how LXRA's AI-assisted workflow compares to traditional photography — without replacing the photographer."
      />

      {/* HERO */}
      <section className="relative overflow-hidden lxra-grain" data-testid={HOW_IT_WORKS.hero}>
        <div className="mx-auto max-w-6xl px-4 sm:px-6 py-20 sm:py-28">
          <Reveal>
            <p className="text-xs tracking-[0.28em] uppercase text-[hsl(var(--lxra-gold))] mb-4">LXRA Workflow</p>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[0.92] text-[hsl(var(--lxra-fg))]">
              <TextReveal text="HOW AI COMPARES TO" />
              <br />
              <TextReveal text="TRADITIONAL PHOTOGRAPHY" delay={0.08} />
            </h1>
            <p className="mt-5 max-w-2xl text-sm sm:text-base text-[hsl(var(--lxra-muted))] leading-relaxed">
              A stage-by-stage look at what changes when planning, shooting, and finishing are supported by
              LXRA&apos;s AI tools — without replacing the photographer.
            </p>
            <div className="mt-7 flex flex-col sm:flex-row gap-3">
              <MagneticButton>
                <a
                  href="#timeline"
                  data-testid={HOW_IT_WORKS.heroPrimaryCta}
                  className="inline-flex items-center h-12 px-6 rounded-xl bg-[hsl(var(--lxra-gold))] text-[#0A0A0A] hover:bg-[hsl(var(--lxra-gold-hover))] transition-colors duration-200 text-sm uppercase tracking-[0.1em] font-semibold"
                >
                  Jump to the Timeline
                  <ArrowRight className="ml-2 h-4 w-4" />
                </a>
              </MagneticButton>
              <a
                href="#voice-guide"
                data-testid={HOW_IT_WORKS.heroSecondaryCta}
                className="inline-flex items-center h-12 px-6 rounded-xl border border-[hsl(var(--lxra-gold))]/50 text-[hsl(var(--lxra-fg))] hover:border-[hsl(var(--lxra-gold))] hover:text-[hsl(var(--lxra-gold))] transition-colors duration-200 text-sm uppercase tracking-[0.1em] font-semibold"
              >
                <Volume2 className="mr-2 h-4 w-4" />
                Play Voice Guide
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* AUDIENCE TOGGLE */}
      <section className="border-y border-[hsl(var(--lxra-border))] bg-[hsl(var(--lxra-bg-2))]">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 py-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs uppercase tracking-[0.2em] text-[hsl(var(--lxra-muted))]">
            See it from your perspective
          </p>
          <AudienceToggle value={audience} onChange={setAudience} />
        </div>
      </section>

      {/* DUAL TRACK TIMELINE */}
      <section className="mx-auto max-w-6xl px-4 sm:px-6 py-16 sm:py-20">
        <Reveal>
          <div className="mb-10 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
            <div className="hidden lg:grid grid-cols-12 w-full text-[11px] uppercase tracking-[0.2em] text-[hsl(var(--lxra-muted))]">
              <span className="col-span-5">Traditional Workflow</span>
              <span className="col-span-2 text-center">Stage</span>
              <span className="col-span-5 text-right text-[hsl(var(--lxra-gold))]">LXRA AI-Powered Workflow</span>
            </div>
          </div>
        </Reveal>
        <DualTrackTimeline
          ref={timelineRef}
          audience={audience}
          activeStage={activeStage}
          onActiveStageChange={setActiveStage}
        />
      </section>

      {/* VOICE GUIDE */}
      <section className="mx-auto max-w-3xl px-4 sm:px-6 py-4 sm:py-8">
        <VoiceGuidePlayer audience={audience} activeStage={activeStage} onStageSelect={goToStage} />
      </section>

      {/* PLATFORM TOUR */}
      <section id="platform-tour" className="mx-auto max-w-6xl px-4 sm:px-6 py-16 sm:py-20" data-testid={HOW_IT_WORKS.tourSection}>
        <Reveal>
          <p className="text-xs tracking-[0.22em] uppercase text-[hsl(var(--lxra-gold))] mb-2">Platform Tour</p>
          <h2 className="font-display text-3xl sm:text-4xl tracking-[0.03em] text-[hsl(var(--lxra-fg))]">
            SEE THE TOOLS IN ACTION.
          </h2>
        </Reveal>
        <div className="mt-8">
          <PlatformTourCarousel />
        </div>
      </section>

      {/* CLOSING CTA */}
      <section
        className="mt-4 border-t border-[hsl(var(--lxra-gold))]/35 bg-[hsl(var(--lxra-bg-2))]"
        data-testid={HOW_IT_WORKS.closingSection}
      >
        <div className="mx-auto max-w-6xl px-4 sm:px-6 py-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <Reveal>
            <p className="text-xs tracking-[0.2em] uppercase text-[hsl(var(--lxra-gold))]">
              {audience === 'client' ? 'Ready to find your photographer?' : 'Ready to grow your studio?'}
            </p>
            <h2 className="font-display text-2xl sm:text-3xl mt-2 tracking-[0.02em] text-[hsl(var(--lxra-fg))]">
              {audience === 'client' ? "LET'S FIND YOUR PHOTOGRAPHER." : 'JOIN THE LXRA MARKETPLACE.'}
            </h2>
          </Reveal>
          <Reveal delay={0.1} className="flex flex-col sm:flex-row items-center gap-3">
            <MagneticButton>
              <Link
                to={audience === 'client' ? '/photographers' : '/signup/photographer'}
                data-testid={HOW_IT_WORKS.closingPrimary}
                className="inline-flex items-center h-12 px-7 rounded-xl bg-[hsl(var(--lxra-gold))] text-[#0A0A0A] hover:bg-[hsl(var(--lxra-gold-hover))] transition-colors duration-200 text-sm uppercase tracking-[0.1em] font-semibold whitespace-nowrap"
              >
                {audience === 'client' ? 'Find a Photographer' : 'Apply to Join'}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </MagneticButton>
            <Link
              to={audience === 'client' ? '/contact' : '/login'}
              data-testid={HOW_IT_WORKS.closingSecondary}
              className="inline-flex items-center h-12 px-6 rounded-xl border border-[hsl(var(--lxra-gold))]/50 text-[hsl(var(--lxra-fg))] hover:border-[hsl(var(--lxra-gold))] hover:text-[hsl(var(--lxra-gold))] transition-colors duration-200 text-sm uppercase tracking-[0.1em] font-medium whitespace-nowrap"
            >
              {audience === 'client' ? 'Have Questions?' : 'Already a Member? Sign In'}
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
